import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useAccountId } from '../../../../redux/slices/appInfo/hooks';
import { usePublishedPagesUsage } from '../../../smartLinks/usePublishedPagesUsage';
import { useMusic24Capabilities } from '../../hooks/useMusic24Capabilities';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { planNameMessages } from '../../config/music24PlanNames';
import { SMLNavUsageKey } from '../smlNavigation';
import { smlNavigationChromeMessages as messages } from '../smlNavigationMessages';

export interface SMLNavUsageCaption {
  text: string;
  /** Set at the limit: the tile shows the lock chip of the cheapest plan that lifts it. */
  lockFeature?: SMLUpgradeFeature;
}

/**
 * The usage captions of the mobile hub tiles (design §6, free-plans n). The numbers come from the
 * plan table, never from this file.
 *
 * Only published pages have a client-side count today (`usePublishedPagesUsage`), so only
 * Smartlinks reads "2 of 3" and can reach the lock chip. Browse left the menu (navigation v2), so
 * it has no caption here. Posts, video credits and social accounts
 * are metered by a server-to-server ledger with no client read, so their tiles name the plain
 * limit, the way the Subscription tab does. A plan with no limit shows no caption. Mount it only
 * where a hub is open: the page count costs two reads.
 */
export function useNavUsageCaptions(): Partial<Record<SMLNavUsageKey, SMLNavUsageCaption>> {
  const intl = useIntl();
  const capabilities = useMusic24Capabilities();
  const accountId = useAccountId();
  const pages = usePublishedPagesUsage(accountId ?? 0, { skip: accountId === null });

  return useMemo(() => {
    // Design §6 captions the Free plan only. A paid plan reads its limits in Settings.
    if (capabilities.plan !== 'free') {
      return {};
    }
    const plan = intl.formatMessage(planNameMessages[capabilities.plan]);
    const captions: Partial<Record<SMLNavUsageKey, SMLNavUsageCaption>> = {};

    // design: your-intelligence h3 — "7 days of history on Free" under Streaming.
    captions.portalHistory = {
      text: intl.formatMessage(messages.usagePortalHistory, { days: capabilities.historyDaysLimit, plan }),
    };
    if (pages.limit !== null && pages.used !== undefined && accountId !== null) {
      captions.pages = {
        text: intl.formatMessage(messages.usagePages, { used: pages.used, limit: pages.limit }),
        lockFeature: pages.used >= pages.limit ? SMLUpgradeFeature.pages : undefined,
      };
    }
    if (capabilities.scheduledPostsPerMonth !== null) {
      captions.scheduledPosts = {
        text: intl.formatMessage(messages.usageScheduledPosts, { limit: capabilities.scheduledPostsPerMonth, plan }),
      };
    }
    if (capabilities.videoCreditsPerMonth !== null) {
      captions.videoCredits = {
        text: intl.formatMessage(messages.usageVideoCredits, { limit: capabilities.videoCreditsPerMonth, plan }),
      };
    }
    if (capabilities.socialAccountsLimit !== null) {
      captions.socialAccounts = {
        text: intl.formatMessage(messages.usageSocialAccounts, { limit: capabilities.socialAccountsLimit, plan }),
      };
    }
    return captions;
  }, [intl, capabilities, pages, accountId]);
}
